import { useCallback, useEffect, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import {
  Dropdown,
  DropdownChip,
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownTrigger,
} from '@mesh.ai/rabbit';
import { L3_PATHS } from 'Deer/constants/routes';
import { useDeerL1ModuleAndTeamType } from 'Deer/hooks/useDeerL1ModuleAndTeamType';
import { useDeerViewId } from 'Deer/hooks/useDeerViewId';
import { useQuery } from 'components/hooks/useQuery';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { setSelectedReviewCycle, useReviewManageSidepanelData } from 'reducers/reviewManageSidepanelSlice';
import {
  setIsCalibrationViewPreviouslySelected,
  setSelectedCalibrationView,
  useTalentPlanningSidepanelData,
} from 'reducers/talentPlanningSidepanelSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import useDeerTeamBasedReviewRoute from 'screens/performanceReview/hooks/useDeerTeamBasedReviewRoute';
import { useGetReviewCyclesAndCalibrationViews } from 'screens/performanceReview/manageAndInsights/ReviewManage/dataHooks';
import { checkDemoReviewCreationTriggeredReviewsEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { getEncodedCohortQueryParam } from 'constants/helper';
import { routes } from 'constants/routes';

import { Divider } from 'uiToolkitV1/Divider';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TagColored } from 'uiToolkitV1/Tag';
import BetaTag from 'uiToolkitV1/Tag/BetaTag';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { setSelectedReviewsForm } from '../slice/ReviewsIntentSlice';

// this is required because when navigating between pages, the setSelectedView(asynchronous) takes time.
// so for a moment the path is different and selected view in redux is different(for eg, direct team path, but company view in redux)
// this is an issue because our applicable review and calibration view list depends on view/cohort.
// and we have to do either persistence or redirection if a specific calibration view is available for current view/cohort or not
// so this is used to identify the times when the combination is correct, and only then apply the logic to persist or redirect
const useIsValidPathAndCohort = () => {
  const cohortDetails = useSelectedView();

  const { l1Module, teamType } = useDeerL1ModuleAndTeamType();

  if (teamType === 'directReports' && cohortDetails?.id === -2) {
    return true;
  }
  if (teamType === 'allReports' && cohortDetails?.id === -3) {
    return true;
  }
  if (l1Module === 'company' && cohortDetails?.id === -1) {
    return true;
  }
  if (teamType === 'view' && ![-1, -2, -3].includes(cohortDetails?.id)) {
    return true;
  }

  return false;
};

const ReviewManageReviewCycleDropdown = () => {
  const { reviewCycleId: queryParamReviewCycleId = '', calibrationViewId: queryParamCalibrationViewId = '' } =
    useQuery();
  const numberReviewCycleId = queryParamReviewCycleId === 'null' ? null : Number(queryParamReviewCycleId);
  const numberCalibrationCycleId = queryParamCalibrationViewId === 'null' ? null : Number(queryParamCalibrationViewId);

  const { selectedReviewCycle } = useReviewManageSidepanelData();
  const { selectedCalibrationView, isCalibrationViewPreviouslySelected } = useTalentPlanningSidepanelData();

  const history = useHistory();
  const isTalentPlanningPage = history.location.pathname.includes(routes.performanceReview.talentPlanning);
  const isDetailedReportPage = history.location.pathname.includes(routes.performanceReview.detailedReports);

  // const isReviewManagePage = history.location.pathname.includes(routes.performanceReview.manage);
  // const { id: intentId } = useSelectReviewsSelectedIntent() ?? {};

  const isCompletionPage = useRouteMatch({
    path: [
      L3_PATHS.team.allReports.review.completionStatus,
      L3_PATHS.team.directReports.review.completionStatus,
      L3_PATHS.company.review.completionStatus,
      L3_PATHS.team.view.review.completionStatus,
    ],
    exact: true,
  });

  const isCalibratedPage = useRouteMatch({
    path: [
      L3_PATHS.team.allReports.review.calibratedResults,
      L3_PATHS.team.directReports.review.calibratedResults,
      L3_PATHS.company.review.calibratedResults,
      L3_PATHS.team.view.review.calibratedResults,
    ],
    exact: true,
  });

  const isPlanOfActionPage = !!useRouteMatch({ path: [L3_PATHS.team.directReports.review.actionPlan], exact: true });

  const dispatch = useDispatch();
  const teamBasedReviewRoute = useDeerTeamBasedReviewRoute();
  const { viewId: viewIdFromQueryParams } = useDeerViewId();

  const cohortDetails = useSelectedView();

  const viewIdToAppend = viewIdFromQueryParams ? `viewId=${viewIdFromQueryParams}&` : '';

  const isValidPathAndCohort = useIsValidPathAndCohort();

  const { data: reviewCyclesAndCalibrationViews1, isLoading: isLoadingList } = useGetReviewCyclesAndCalibrationViews({
    cohortId: viewIdFromQueryParams || cohortDetails?.id,
    isPlanOfAction: isPlanOfActionPage,
  });

  const isReviewViewCalibratedPage = useRouteMatch({
    path: L3_PATHS.team.view.review.calibratedResults,
    exact: true,
  });

  const isReviewViewCalibrationPage = useRouteMatch({
    path: L3_PATHS.team.view.review.calibrationView,
    exact: true,
  });

  const reviewCyclesAndCalibrationViews = useMemo(() => {
    const reviewCycles = isLoadingList ? [] : (reviewCyclesAndCalibrationViews1?.reviewCycles ?? []);
    const calibrationViews = isLoadingList ? [] : (reviewCyclesAndCalibrationViews1?.calibrationViews ?? []);

    return {
      reviewCycles,
      calibrationViews,
    };
  }, [reviewCyclesAndCalibrationViews1, isLoadingList]);

  const hasCalibrationViews = reviewCyclesAndCalibrationViews.calibrationViews.length > 0;

  const getTagNameForReviewCycle = (reviewCycleId) => {
    if (!checkDemoReviewCreationTriggeredReviewsEnabled() || cohortDetails?.id !== -1) {
      return '';
    }
    if (reviewCycleId === 1104) {
      return 'Recurring';
    }
    if (reviewCycleId === 1103) {
      return 'Continuous';
    }
    return '';
  };

  const reviewCycleOptions = useMemo(() => {
    const options = (reviewCyclesAndCalibrationViews?.reviewCycles ?? []).map(({ id, name }) => ({
      value: id,
      label: name,
      tag: getTagNameForReviewCycle(id),
    }));
    return options;
  }, [reviewCyclesAndCalibrationViews?.reviewCycles]);

  const calibrationCycleOptions = useMemo(() => {
    const options = (reviewCyclesAndCalibrationViews?.calibrationViews ?? []).map(({ id, name }) => ({
      value: id,
      label: name,
    }));
    return options;
  }, [reviewCyclesAndCalibrationViews?.calibrationViews]);

  const selectedReviewCycleOption = reviewCycleOptions.find(({ value }) => value === numberReviewCycleId) ?? null;
  const selectedCalibrationCycleOption =
    calibrationCycleOptions.find(({ value }) => value === numberCalibrationCycleId) ?? null;

  const selectedValue = isTalentPlanningPage ? selectedCalibrationCycleOption : selectedReviewCycleOption;

  const onReviewCycleChange = useCallback(
    (reviewCycle) => {
      dispatch(setSelectedReviewCycle(reviewCycle));

      if (reviewCycle && cohortDetails) {
        const cohortQueryString = getEncodedCohortQueryParam(cohortDetails, history.location, false);
        history.push({
          pathname: teamBasedReviewRoute,
          search: `${viewIdToAppend}${cohortQueryString}&reviewCycleId=${reviewCycle.uuid}`,
        });
      }
    },
    [dispatch, cohortDetails, history, teamBasedReviewRoute, viewIdToAppend]
  );

  const onReviewCycleChangeById = useCallback(
    (reviewCycleId) => {
      /*
        we are setting selected review form as null here because 
        ReviewManageTimelineFilter component is mounting and unmounting
        causing selected review form to use the previously selected review form
        ENG-18173 for reference
      */
      dispatch(setSelectedReviewsForm(null));
      const reviewCycle = reviewCyclesAndCalibrationViews?.reviewCycles.find(({ id }) => id === reviewCycleId) ?? null;

      onReviewCycleChange(reviewCycle);
    },
    [reviewCyclesAndCalibrationViews?.reviewCycles, onReviewCycleChange, dispatch]
  );

  const onCalibrationCycleChange = useCallback(
    (calibrationView) => {
      dispatch(setSelectedCalibrationView(calibrationView));

      const cohortQueryString = getEncodedCohortQueryParam(cohortDetails, history.location, true);

      const urlSearchParams = new URLSearchParams(cohortQueryString);
      urlSearchParams.set('calibrationViewId', calibrationView.uuid);
      if (viewIdToAppend) {
        urlSearchParams.set('viewId', viewIdFromQueryParams);
      }
      const search = urlSearchParams.toString();

      const getNewNavRedirectPageLink = () => {
        if (isReviewViewCalibratedPage || isReviewViewCalibrationPage) {
          return L3_PATHS.team.view.review.calibrationView;
        }
        return L3_PATHS.company.review.calibrationView;
      };

      history.push({
        pathname: checkNewNavEnabled() ? getNewNavRedirectPageLink() : routes.performanceReview.talentPlanning,
        search,
      });
    },
    [
      dispatch,
      cohortDetails,
      history,
      viewIdToAppend,
      viewIdFromQueryParams,
      isReviewViewCalibratedPage,
      isReviewViewCalibrationPage,
    ]
  );

  const onCalibrationCycleChangeById = useCallback(
    (calibrationViewId) => {
      const reviewCycle =
        reviewCyclesAndCalibrationViews?.calibrationViews.find(({ id }) => id === calibrationViewId) ?? null;

      onCalibrationCycleChange(reviewCycle);
    },
    [reviewCyclesAndCalibrationViews?.calibrationViews, onCalibrationCycleChange]
  );

  // to persist the selection of calibration view
  useEffect(() => {
    if (!isValidPathAndCohort || isLoadingList) {
      return;
    }
    if (isTalentPlanningPage) {
      dispatch(setIsCalibrationViewPreviouslySelected(true));
    } else {
      dispatch(setIsCalibrationViewPreviouslySelected(false));
    }
  }, [dispatch, isLoadingList, isTalentPlanningPage, isValidPathAndCohort]);

  useEffect(() => {
    // to prevent unintended navigation on unmount to other app pages
    if (
      !isValidPathAndCohort ||
      (!isTalentPlanningPage && !isCalibratedPage && !isCompletionPage && !isPlanOfActionPage && !isDetailedReportPage)
    ) {
      return;
    }
    if (isLoadingList || reviewCyclesAndCalibrationViews?.reviewCycles?.length === 0) {
      return;
    }

    const hasQueryParamReviewCycleId = queryParamReviewCycleId && queryParamReviewCycleId !== 'null';
    const hasQueryParamCalibrationViewId = queryParamCalibrationViewId && queryParamCalibrationViewId !== 'null';

    const hasReduxStoreReviewCycle = selectedReviewCycle;
    const reduxStoreReviewCycleId = selectedReviewCycle?.uuid;

    const hasReduxStoreCalibrationView = selectedCalibrationView;
    const reduxStoreCalibrationViewId = selectedCalibrationView?.uuid;

    if (
      (hasQueryParamReviewCycleId && queryParamReviewCycleId === reduxStoreReviewCycleId) ||
      (hasQueryParamCalibrationViewId && queryParamCalibrationViewId === reduxStoreCalibrationViewId)
    ) {
      return;
    }

    const isValidReviewCycleId = (reviewCycleId) =>
      reviewCyclesAndCalibrationViews.reviewCycles.find((rc) => rc.uuid === reviewCycleId);

    const isValidCalibrationViewId = (calibrationViewId) =>
      reviewCyclesAndCalibrationViews.calibrationViews.find((av) => av.uuid === calibrationViewId);

    // when navigating from calibration page and clicking on l1, it will first go to review manage page only.
    // as that is the primary route to go to while navigating to reviews.
    // remember: review manage and review calibration are essentially two different pages,
    // but we have to make them look like UX wise as single page.
    // review manage being primary page is the place to land on navigating from anywhere else
    // where as review calibration can only be navigated by dropdown.
    // so, to persist review calibration page on navigation from l1,l2(when we are already in a calibration page),
    // we'll have to go through a redirect to review manage page only.
    // we'll track if previously we were on calibration page, along with some other condition(if valid etc),
    // then we'll send to review calibration page from this special condition block.
    if (
      reduxStoreCalibrationViewId &&
      isValidCalibrationViewId(reduxStoreCalibrationViewId) &&
      isCalibrationViewPreviouslySelected &&
      !hasQueryParamCalibrationViewId &&
      !isTalentPlanningPage &&
      !isCompletionPage
    ) {
      onCalibrationCycleChange(
        reviewCyclesAndCalibrationViews.calibrationViews.find((av) => av.uuid === reduxStoreCalibrationViewId)
      );
      return;
    }

    // query param takes precedence over redux store
    if (!isTalentPlanningPage) {
      if (hasQueryParamReviewCycleId && queryParamReviewCycleId !== reduxStoreReviewCycleId) {
        if (isValidReviewCycleId(queryParamReviewCycleId)) {
          onReviewCycleChange(
            reviewCyclesAndCalibrationViews.reviewCycles.find((rc) => rc.uuid === queryParamReviewCycleId)
          );
        } else {
          onReviewCycleChange(reviewCyclesAndCalibrationViews.reviewCycles[0]);
        }
      } else if (!hasQueryParamReviewCycleId && hasReduxStoreReviewCycle) {
        onReviewCycleChange(
          reviewCyclesAndCalibrationViews.reviewCycles.find((rc) => rc.uuid === reduxStoreReviewCycleId)
        );
      } else {
        onReviewCycleChange(reviewCyclesAndCalibrationViews.reviewCycles[0]);
      }
    }

    if (isTalentPlanningPage) {
      if (hasQueryParamCalibrationViewId && queryParamCalibrationViewId !== reduxStoreCalibrationViewId) {
        if (isValidCalibrationViewId(queryParamCalibrationViewId)) {
          onCalibrationCycleChange(
            reviewCyclesAndCalibrationViews.calibrationViews.find((av) => av.uuid === queryParamCalibrationViewId)
          );
        } else {
          onCalibrationCycleChange(reviewCyclesAndCalibrationViews.calibrationViews[0]);
        }
      } else if (!hasQueryParamCalibrationViewId && hasReduxStoreCalibrationView) {
        onCalibrationCycleChange(
          reviewCyclesAndCalibrationViews.calibrationViews.find((av) => av.uuid === reduxStoreCalibrationViewId)
        );
      } else {
        onCalibrationCycleChange(reviewCyclesAndCalibrationViews.calibrationViews[0]);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    onReviewCycleChange,
    queryParamReviewCycleId,
    queryParamCalibrationViewId,
    reviewCyclesAndCalibrationViews,
    selectedReviewCycle,
    selectedCalibrationView,
    onCalibrationCycleChange,
    onReviewCycleChange,
    isTalentPlanningPage,
    isCompletionPage,
    isCalibratedPage,
    isDetailedReportPage,
  ]);

  if (isLoadingList) {
    return (
      <Flex alignItems="center" flexGrow={1}>
        <SkeletonLoader skeletons={['10px']} />
      </Flex>
    );
  }

  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <DropdownChip className="chip-dropdown">
          <TextBodyMedium bold color="text.g2" className="group-data[state=open]:text-[#004F67]">
            Cycle :
          </TextBodyMedium>

          <TextBodyMedium bold color="text.g1">
            {selectedValue?.label ?? ''}
          </TextBodyMedium>
        </DropdownChip>
      </DropdownTrigger>
      <DropdownContent
        align="start"
        style={{
          maxHeight: hasCalibrationViews ? '408px' : '280px',
          maxWidth: '100%',
        }}
      >
        <Flex
          flexDir="column"
          flexGrow={1}
          // align="start"
          style={{
            maxHeight: hasCalibrationViews ? '200px' : '276px',
            maxWidth: '100%',
            // overflowY: 'auto',
            // minWidth: '220px',
          }}
        >
          <Box padding="8px">
            <TextLabel bold color="text.g2">
              REVIEW CYCLE
            </TextLabel>
          </Box>
          <DropdownRadioGroup
            value={`reviewCycle${selectedValue?.value}`}
            style={{
              flexGrow: 1,
              overflowY: 'auto',
            }}
          >
            {reviewCycleOptions?.map(({ label, value, tag }) => (
              <DropdownRadioItem
                key={value}
                value={`reviewCycle${value}`}
                onClick={() => onReviewCycleChangeById(value)}
              >
                {label}
                {tag && (
                  <TagColored color="grey" marginLeft="4px">
                    {tag}
                  </TagColored>
                )}
              </DropdownRadioItem>
            ))}
          </DropdownRadioGroup>
        </Flex>

        {hasCalibrationViews && (isReviewViewCalibrationPage || isCalibratedPage || isTalentPlanningPage) && (
          <Flex flexDir="column" flexGrow={1} maxH="200px" width="100%">
            <Divider opacity={1} bgColor="#EBEFF3" my="4px" />
            <Box padding="8px">
              <TextLabel bold color="text.g2">
                CALIBRATION CYCLE
              </TextLabel>
            </Box>
            <DropdownRadioGroup
              value={`calibrationView${selectedValue?.value}`}
              style={{
                flexGrow: 1,
                overflowY: 'auto',
              }}
            >
              {calibrationCycleOptions?.map(({ label, value, isBeta }) => (
                <DropdownRadioItem
                  key={value}
                  value={`calibrationView${value}`}
                  onClick={() => onCalibrationCycleChangeById(value)}
                >
                  {label}
                  {isBeta && (
                    <Box marginLeft="8px">
                      <BetaTag />
                    </Box>
                  )}
                </DropdownRadioItem>
              ))}
            </DropdownRadioGroup>
          </Flex>
        )}
      </DropdownContent>
    </Dropdown>
  );
};

export { ReviewManageReviewCycleDropdown };
